import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const RED = "bg-rose-50", AMBER = "bg-amber-50", GREEN = "bg-emerald-50";

function Section({ title, note, headers, rows, testid }) {
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5" data-testid={testid}>
      <h2 className="text-base font-semibold text-slate-900">{title} <span className="text-slate-400 font-normal">({rows.length})</span></h2>
      {note && <p className="text-xs text-slate-500 mt-0.5">{note}</p>}
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b">{headers.map((h) => <th key={h} className="py-2 pr-3 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={headers.length} className="py-3 text-slate-400">None</td></tr>}
            {rows.map(([bg, cells], i) => (
              <tr key={i} className={`${bg} border-b border-white`} data-testid={`${testid}-row-${i}`}>
                {cells.map((v, j) => <td key={j} className="py-2 px-2 align-top">{v}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const groupTags = (x) => (
  <div className="flex flex-wrap gap-1">{x.groups.map((g) => (
    <span key={g.name} className={`text-xs px-1.5 py-0.5 rounded font-medium ${g.blocking ? "bg-rose-600 text-white" : "bg-amber-200 text-amber-900"}`}>{g.name}</span>
  ))}</div>
);

export default function DailyReport() {
  const { user } = useAuth();
  const [r, setR] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    setBusy(true);
    api.get("/reports/daily").then(({ data }) => { setR(data); setErr(""); })
      .catch(() => setErr("Could not load the report. It will retry automatically."))
      .finally(() => setBusy(false));
  }, []);
  const [dl, setDl] = useState(false);
  const download = async () => {
    setDl(true);
    try {
      const { data } = await api.get("/reports/daily/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CrewOnShip-Daily-Report-${r?.date || "today"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the PDF, please try again");
    } finally {
      setDl(false);
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);
  if (!r) return <div className="p-6 text-slate-500" data-testid="daily-report-loading">{err || "Loading report…"} {err && <Button size="sm" variant="outline" onClick={load} data-testid="daily-report-retry-btn">Retry</Button>}</div>;
  const ownOnly = user?.role?.domain === "Sourcing";
  const name = (x) => (!ownOnly || x.created_by === user?.id
    ? <Link to={`/candidates/${x.id}`} className="font-medium text-slate-900 hover:underline">{x.name}</Link>
    : <span className="font-medium text-slate-900">{x.name}</span>);
  const ready = (x) => [name(x), x.rank, x.vessel, x.stage];
  return (
    <div className="space-y-5 max-w-5xl" data-testid="daily-report-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">Daily Report</h1>
          <p className="text-sm text-slate-500" data-testid="daily-report-meta">{r.date} · {r.total_active} candidate(s) in pipeline · updated {new Date(r.generated_at).toLocaleTimeString()} (auto-refreshes every 30s)</p>
        </div>
        <div className="flex gap-2">
        <Button onClick={download} disabled={dl} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid="daily-report-download-pdf-btn"><Download className="w-4 h-4 mr-1" /> {dl ? "Preparing…" : "Download PDF"}</Button>
        <Button variant="outline" onClick={load} disabled={busy} data-testid="daily-report-refresh-btn"><RefreshCw className={`w-4 h-4 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh</Button>
        </div>
      </div>
      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="daily-report-error">{err}</div>}
      <section className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5" data-testid="report-stages">
        <h2 className="text-base font-semibold text-slate-900">Candidates by stage</h2>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {r.stages.map((s) => (
            <div key={s.stage} className="border border-slate-200 rounded-lg px-3 py-2" data-testid={`report-stage-${s.stage.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="text-xs text-slate-500">{s.stage}</div><div className="text-xl font-semibold text-slate-900">{s.count}</div>
            </div>
          ))}
        </div>
      </section>
      <Section title="Incomplete checklist groups" note="Red = blocking the current stage · Amber = needed for an upcoming stage" headers={["Candidate", "Stage", "Pending groups"]}
        rows={r.incomplete.map((x) => [x.blocking ? RED : AMBER, [name(x), x.stage, groupTags(x)]])} testid="report-incomplete" />
      <Section title="Expiring candidate documents" note={`Red = expired · Amber = expires within 30 days · ${r.valid_docs} document(s) valid`} headers={["Candidate", "Document", "Expiry", "Status"]}
        rows={r.expiring.map((x) => [x.status === "expired" ? RED : AMBER, [name(x), x.document, x.expiry, x.status === "expired" ? "Expired" : `${x.days_left} days left`]])} testid="report-expiring" />
      <Section title="Ready to join — awaiting Management sign-off" headers={["Candidate", "Rank", "Vessel", "Stage"]} rows={r.awaiting_signoff.map((x) => [AMBER, ready(x)])} testid="report-awaiting" />
      <Section title="Ready to join — signed off, not yet joined" headers={["Candidate", "Rank", "Vessel", "Stage"]} rows={r.signed_off.map((x) => [GREEN, ready(x)])} testid="report-signed" />
      <Section title="Joined today" headers={["Candidate", "Rank"]} rows={r.joined_today.map((x) => [GREEN, [name(x), x.rank]])} testid="report-joined" />
    </div>
  );
}
